import { Show } from '@atlaskit/primitives/responsive';
import { Breadcrumbs, BreadcrumbsItem } from '@atlassian/mpac-primitives';
import { useHistory } from '@atlassian/mpac-ui';
import { css } from '@compiled/react';
import React, { useCallback } from 'react';

import {
  ContentContainer,
  Description,
  Heading,
  HeroBackground,
  HeroContainer,
  Image,
  ImageContainerDesktop,
  ImageContainerMobile,
} from './Hero.styles';

export type HeroProps = {
  backgroundColor: string;
  title: string;
  primaryBreadcrumbTitle?: string;
  primaryBreadcrumbHref?: string;
  primaryBreadcrumbDataTestId?: string;
  secondaryBreadcrumbTitle: string;
  description: string;
  imageUrl: string;
  imageAltText: string;
  isBreadcrumbCompact?: boolean;
  hasBackground?: boolean;
  heroContentGridStartDesktop?: number;
  heroContentGridEndDesktop?: number;
  isImageBelowDescription?: boolean;
  useCompactSpacing?: boolean;
};

const breadcrumbsStyles = css({
  gridColumn: '1 / 8',
});

export const Hero = ({
  primaryBreadcrumbTitle = 'Marketplace',
  primaryBreadcrumbHref = '/',
  primaryBreadcrumbDataTestId,
  secondaryBreadcrumbTitle,
  backgroundColor,
  title,
  description,
  imageUrl,
  imageAltText,
  isBreadcrumbCompact = true,
  hasBackground = true,
  heroContentGridStartDesktop = 2,
  heroContentGridEndDesktop = 9,
  isImageBelowDescription = false,
  useCompactSpacing = false,
}: HeroProps) => {
  const history = useHistory();

  const handleBreadcrumbsClick = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.preventDefault();
    history?.push(primaryBreadcrumbHref);
  };
  const setFocusOnHeading = useCallback(
    (ref) => {
      if (history?.action === 'PUSH' && ref) setTimeout(() => ref?.focus());
    },
    [history?.location]
  );

  const image = <Image alt={imageAltText} src={imageUrl} width="1056" height="1056" />;

  const BreadcrumbsComponent = () => {
    return (
      <Breadcrumbs css={!isBreadcrumbCompact && breadcrumbsStyles}>
        <BreadcrumbsItem
          href={primaryBreadcrumbHref}
          text={primaryBreadcrumbTitle}
          onClick={handleBreadcrumbsClick}
          testId={primaryBreadcrumbDataTestId}
        />
        <BreadcrumbsItem text={secondaryBreadcrumbTitle} onClick={(e) => e.preventDefault()} />
      </Breadcrumbs>
    );
  };

  return (
    <HeroBackground backgroundColor={backgroundColor} hasBackground={hasBackground}>
      <HeroContainer useCompactSpacing={useCompactSpacing}>
        {!isBreadcrumbCompact && <BreadcrumbsComponent />}
        <ContentContainer
          heroContentGridStartDesktop={heroContentGridStartDesktop}
          heroContentGridEndDesktop={heroContentGridEndDesktop}
        >
          {isBreadcrumbCompact && <BreadcrumbsComponent />}
          <Heading tabIndex={-1} ref={setFocusOnHeading}>
            {title}
          </Heading>
          {!isImageBelowDescription && (
            <Show below="sm">
              <ImageContainerMobile>{image}</ImageContainerMobile>
            </Show>
          )}
          <Description>{description}</Description>
          {isImageBelowDescription && (
            <Show below="sm">
              <ImageContainerMobile>{image}</ImageContainerMobile>
            </Show>
          )}
        </ContentContainer>
        <ImageContainerDesktop useCompactSpacing={useCompactSpacing}>{image}</ImageContainerDesktop>
      </HeroContainer>
    </HeroBackground>
  );
};
